import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useCustomerGroupsQuery, useMemberTiersQuery } from "../api/queries";

export function CustomersFilter() {
  const nav = useNavigate({ from: "/app/customers" });
  const search = useSearch({ from: "/app/customers" }) as {
    limit?: number;
    offset?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
    filters?: FilterConditionDTO[] | string;
  };

  const tiers = useMemberTiersQuery({ limit: 100 });
  const tierOptions = tiers.data?.data ?? [];
  const groups = useCustomerGroupsQuery({ limit: 200 });
  const groupOptions = groups.data?.data ?? [];

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const nameFilter = findCondition(filters, "name");
  const memberTierIdFilter = findCondition(filters, "memberTierId");
  const groupIdFilter = findCondition(filters, "groupId");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    let nextFilters = removeConditions(filters, "name") ?? [];
    if (val) {
      nextFilters = upsertCondition(nextFilters, {
        field: "name",
        op: "contains",
        value: val,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as Record<string, unknown>,
    });
  }, 400);

  const handleTierChange = (value: string) => {
    let nextFilters = removeConditions(filters, "memberTierId") ?? [];
    if (value && value !== "all") {
      nextFilters = upsertCondition(nextFilters, {
        field: "memberTierId",
        op: "eq",
        value,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as Record<string, unknown>,
    });
  };

  const clearFilters = () => {
    nav({
      search: { ...search, offset: 0, filters: undefined } as Record<
        string,
        unknown
      >,
    });
  };

  const handleGroupChange = (value: string) => {
    let nextFilters = removeConditions(filters, "groupId") ?? [];
    if (value && value !== "all") {
      nextFilters = upsertCondition(nextFilters, {
        field: "groupId",
        op: "eq",
        value,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as Record<string, unknown>,
    });
  };

  const tierValue = (memberTierIdFilter?.value as string) ?? "";
  const groupValue = (groupIdFilter?.value as string) ?? "";
  const hasFilter = Boolean(
    nameFilter?.value || memberTierIdFilter?.value || groupIdFilter?.value,
  );

  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 px-2">
      <Input
        placeholder="ຄົ້ນຫາຊື່ ຫຼື ອີເມວ..."
        defaultValue={searchValue}
        onChange={(e) => debounced(e.target.value)}
        className="h-8 sm:max-w-2xs"
      />
      <Select value={tierValue || "all"} onValueChange={handleTierChange}>
        <SelectTrigger className="h-8 w-[180px]">
          <SelectValue placeholder="ລະດັບສະມາຊິກ" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">ທັງໝົດ</SelectItem>
          {tierOptions.map((t) => {
            const n = t.name as { lo?: string; th?: string; en?: string };
            const label = n?.lo ?? n?.th ?? n?.en ?? t.id;
            return (
              <SelectItem key={t.id} value={t.id}>
                {label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <Select value={groupValue || "all"} onValueChange={handleGroupChange}>
        <SelectTrigger className="h-8 w-[180px]">
          <SelectValue placeholder="ກຸ່ມລູກຄ້າ" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">ທຸກກຸ່ມ</SelectItem>
          {groupOptions.map((g) => {
            const n = g.name as { lo?: string; th?: string; en?: string };
            const label = n?.lo ?? n?.th ?? n?.en ?? g.id;
            return (
              <SelectItem key={g.id} value={g.id}>
                {label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {hasFilter && (
        <Button variant="ghost" size="sm" onClick={clearFilters}>
          <XIcon className="h-4 w-4" /> ລ້າງຕົວກອງ
        </Button>
      )}
    </div>
  );
}
